import { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as XLSX from "xlsx";
import CDRNetworkGraph from "./CDRNetworkGraph";
import CDRTimeline from "./CDRTimeline";
import CDRMultiAnalysis from "./CDRMultiAnalysis";
import CDRUpToFiveAnalysis from "./CDRUpToFiveAnalysis";
import CDRMap from "./CDRMap";
import CdrFeaturesGuide from "./CdrFeaturesGuide";
import CdrBannerAd from "./CdrBannerAd";
import { runSponsoredExport } from "./CdrExportAd";
import CdrTopAd from "./CdrTopAd";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SimpleTableWithFooter from "./SimpleTableWithFooter";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  EXCEL_ACCEPT,
  PATTERNS,
  toStr,
  isSupportedDatasetFile,
  getScientificWarningInfo,
  parseDatasetByExtension,
  loadDatasetFromFile,
  getExtensionFromPath,
  ensureSupportedExtension,
  normalizeMSISDN,
  guessOperatorByPrefix,
  validateAgainstPattern,
  detectCompanyByHeaders,
  buildNormalizedRowsByCompany,
  analyzeCDR,
  formatInsightDate,
  analyzeInsights,
} from "./cdrEngine";

const styles = {
  page: { padding: 16, fontFamily: "system-ui, Arial, sans-serif" },
  headingMain: { fontSize: 30, lineHeight: 1.2, margin: "0 0 8px" },
  headingSection: { fontSize: 22, lineHeight: 1.2, margin: "0 0 8px" },
  
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    background: "#fff",
  },
  row: { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" },
  btn: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #111827",
    background: "#111827",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 800,
  },
  btnGhost: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#111827",
    cursor: "pointer",
    fontWeight: 800,
  },
  btnDanger: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #b91c1c",
    background: "#b91c1c",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 900,
  },
  badge: {
    display: "inline-flex",
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid #d1d5db",
    background: "#f9fafb",
    fontSize: 12,
    fontWeight: 900,
  },
  tabs: { display: "flex", gap: 8, flexWrap: "wrap" },
  modeTabs: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 },
  modeBtn: (active) => ({
    padding: "10px 14px",
    borderRadius: 10,
    border: active ? "1px solid #111827" : "1px solid #d1d5db",
    background: active ? "#111827" : "#fff",
    color: active ? "#fff" : "#111827",
    cursor: "pointer",
    fontWeight: 900,
  }),
  tab: (active) => ({
    padding: "8px 12px",
    borderRadius: 10,
    border: active ? "1px solid #111827" : "1px solid #d1d5db",
    background: active ? "#111827" : "#fff",
    color: active ? "#fff" : "#111827",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 13,
  }),
  tableWrap: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    overflowY: "auto",
    maxHeight: 520,
    background: "#ffffff"
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, color: "#111827" },
  th: {
    position: "sticky",
    top: 0,
    background: "#111827",
    color:"#ffffff",
    borderBottom: "1px solid #e5e7eb",
    padding: "10px 8px",
    textAlign: "left",
    whiteSpace: "nowrap",
   
  },
  rawTd:{
    fontFamily:'monospace',
     color:"#111827",
    background:"#ffffff"
  }
  ,
  td: {
    borderBottom: "1px solid #ddd5d4ff",
    padding: "8px 8px",
    verticalAlign: "top",
    whiteSpace: "nowrap",
    color:"#111827",
    background:"#ffffff"
  },
  tfoot: { background: "#f8fafc", borderTop: "2px solid #e5e7eb", fontWeight: 900 },
  muted: { color: "#9ca3af", fontSize: 13 },
  danger: { color: "#b91c1c", fontSize: 13, fontWeight: 900 },
  ok: { color: "#065f46", fontSize: 13, fontWeight: 900 },
  warnBox: { border: "1px solid #fecaca", background: "#fff1f2", padding: 12, borderRadius: 12 },
  field: { border: "1px solid #060708ff", borderRadius: 10, padding: "10px 12px", minWidth: 200 },
  label: { fontSize: 12, fontWeight: 900, marginBottom: 6 },
  input: { width: "100%", border: "none", outline: "none", fontSize: 14, background: "transparent" },
  centerWrap: {
    minHeight: 260,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
};

const cardSx = {
  p: { xs: 1, sm: 2 },
  mb: 1.5,
  borderRadius: 3,
  border: 1,
  borderColor: "divider",
  boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.08)",
};

const btnBaseSx = { textTransform: "none", borderRadius: 2, fontWeight: 700 };

function SurfaceCard({ children, sx }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, ...sx }}>
      {children}
    </Paper>
  );
}

function AppButton({ tone = "primary", sx, ...props }) {
  if (tone === "ghost") {
    return (
      <Button
        size="small"
        variant="outlined"
        sx={{ ...btnBaseSx, borderColor: "divider", color: "text.primary", ...sx }}
        {...props}
      />
    );
  }
  if (tone === "danger") {
    return <Button size="small" variant="contained" color="error" sx={{ ...btnBaseSx, ...sx }} {...props} />;
  }
  return <Button size="small" variant="contained" sx={{ ...btnBaseSx, ...sx }} {...props} />;
}

function AppBadge({ label }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      sx={{ fontWeight: 700, borderColor: "divider", bgcolor: "action.hover", color: "text.primary" }}
    />
  );
}


function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function buildErrorReportText(validation, fileName, detectedByPrefix, detectedByHeaders) {
  const lines = [];
  lines.push("CDR Excel Format Validation Report");
  lines.push("================================");
  lines.push(`File: ${fileName || "-"}`);
  lines.push(`Prefix Guess: ${detectedByPrefix}`);
  lines.push(`Header Guess: ${detectedByHeaders}`);
  lines.push("");
  lines.push(`Detected Company Pattern: ${validation?.company || "-"}`);
  lines.push(`Status: ${validation?.ok ? "PASS" : "FAIL"}`);
  lines.push(`Expected Columns Count: ${validation?.expectedCount ?? 0}`);
  lines.push(`Found Columns Count: ${validation?.actualCount ?? 0}`);
  lines.push("");
  if (validation && !validation.ok) {
    lines.push("Missing Columns (exact):");
    lines.push(validation.missing.length ? " - " + validation.missing.join("\n - ") : " - None");
    lines.push("");
    lines.push("Extra Columns (not allowed):");
    lines.push(validation.extra.length ? " - " + validation.extra.join("\n - ") : " - None");
    lines.push("");
  }
  lines.push("Expected Columns:");
  lines.push(" - " + (validation?.expected || []).join("\n - "));
  lines.push("");
  lines.push("Actual Columns:");
  lines.push(" - " + (validation?.actualHeaders || []).join("\n - "));
  return lines.join("\n");
}

const PDF_COLORS = {
  ink: [15, 23, 42],
  muted: [71, 85, 105],
  accent: [14, 116, 144],
  accentSoft: [224, 242, 254],
  white: [255, 255, 255],
};

function addPdfBranding(doc, text = "https://www.cyberpunk.pk/") {
  const pageCount = doc.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    doc.setDrawColor(203, 213, 225);
    doc.line(36, pageH - 36, pageW - 36, pageH - 36);
    doc.setTextColor(...PDF_COLORS.muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Authorized analysis only", 36, pageH - 22);
    doc.text(`${text}  •  Page ${i} of ${pageCount}`, pageW - 36, pageH - 22, { align: "right" });
  }
}

function pdfTableOptions(overrides = {}) {
  return {
    theme: "striped",
    margin: { left: 36, right: 36, bottom: 48 },
    styles: { fontSize: 8, cellPadding: 4, textColor: PDF_COLORS.ink, overflow: "linebreak" },
    headStyles: { fillColor: PDF_COLORS.ink, textColor: PDF_COLORS.white, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    footStyles: { fillColor: PDF_COLORS.accentSoft, textColor: PDF_COLORS.ink, fontStyle: "bold" },
    ...overrides,
  };
}

function drawPdfMetadata(doc, metadata, pageWidth) {
  const startY = 96;
  const lineHeight = 11.5;
  const availableWidth = pageWidth - 72;
  const lines = metadata.flatMap((item) => doc.splitTextToSize(item, availableWidth));

  lines.forEach((line, index) => {
    doc.text(line, 36, startY + (index * lineHeight));
  });

  return {
    startY,
    bottomY: startY + (Math.max(lines.length, 1) - 1) * lineHeight + 4,
    lineCount: lines.length,
  };
}

function drawPdfSectionTitle(doc, title, minimumY = 0) {
  const pageH = doc.internal.pageSize.getHeight();
  let y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 22 : 118;
  y = Math.max(y, minimumY);
  if (y > pageH - 88) {
    doc.addPage();
    y = 48;
  }
  doc.setTextColor(...PDF_COLORS.ink);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(title, 36, y);
  doc.setDrawColor(...PDF_COLORS.accent);
  doc.setLineWidth(1.5);
  doc.line(36, y + 5, 88, y + 5);

  if (doc.cdrReportLayout) {
    doc.cdrReportLayout.sections.push({
      title,
      y,
      page: doc.internal.getCurrentPageInfo().pageNumber,
    });
  }

  return y + 12;
}

export function buildCdrPdfReport({
  filteredAnalysis,
  filteredInsights,
  validation,
  fileName,
  fromDate,
  toDate,
  imeiFilter,
  bPartySearch,
  filteredNormRows,
  parseMeta,
  topN,
  mfcFooter,
  mflFooter,
  generatedAt = new Date(),
}) {
  if (!filteredAnalysis) return null;

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFillColor(...PDF_COLORS.ink);
  doc.rect(0, 0, pageW, 82, "F");
  doc.setTextColor(...PDF_COLORS.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("CDR Analysis Report", 36, 34);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`${validation?.company || "Unknown"} operator • ${filteredNormRows.length} analyzed rows`, 36, 54);
  doc.setFontSize(8);
  doc.text("Cyberpunk CDR Analyzer", pageW - 36, 34, { align: "right" });
  doc.text("Lawful, authorized analysis", pageW - 36, 50, { align: "right" });

  doc.setTextColor(...PDF_COLORS.muted);
  doc.setFontSize(8.5);
  const metadata = [
    `Source: ${fileName || "-"}`,
    fromDate || toDate
      ? `Period: ${fromDate || "Earliest record"} to ${toDate || "Latest record"}`
      : "Period: All dates",
    `Filters: IMEI ${imeiFilter || "None"} • B-Party ${bPartySearch || "None"}`,
    `Header row: ${(parseMeta?.headerRowIndex || 0) + 1} • Generated: ${generatedAt.toLocaleString()}`,
  ];
  const metadataLayout = drawPdfMetadata(doc, metadata, pageW);
  const firstSectionY = metadataLayout.bottomY + 22;
  doc.cdrReportLayout = {
    metadata: metadataLayout,
    sections: [],
  };

  if (filteredInsights) {
    const startY = drawPdfSectionTitle(doc, "Analysis overview", firstSectionY);
    autoTable(doc, pdfTableOptions({
      startY,
      head: [["First seen", "Last seen", "Calls", "SMS", "Duration (min)", "Active days"]],
      body: [[
        formatInsightDate(filteredInsights.overview.firstSeen),
        formatInsightDate(filteredInsights.overview.lastSeen),
        filteredInsights.overview.calls,
        filteredInsights.overview.sms,
        filteredInsights.overview.durationMinutes,
        filteredInsights.overview.activeDays,
      ]],
    }));
  }

  let startY = drawPdfSectionTitle(doc, `Most frequent contacts • Top ${topN}`, firstSectionY);
  autoTable(doc, pdfTableOptions({
    startY,
    head: [["B-Party", "In calls", "Out calls", "In SMS", "Out SMS", "Total"]],
    body: (filteredAnalysis.mfcAll || []).slice(0, topN).map((r) => [r.bParty, r.inCall, r.outCall, r.inSms, r.outSms, r.total]),
    foot: [["Grand total", mfcFooter?.inCall ?? 0, mfcFooter?.outCall ?? 0, mfcFooter?.inSms ?? 0, mfcFooter?.outSms ?? 0, mfcFooter?.total ?? 0]],
    showFoot: "lastPage",
  }));

  startY = drawPdfSectionTitle(doc, `Short codes and service IDs • Top ${topN}`);
  autoTable(doc, pdfTableOptions({
    startY,
    head: [["Short code", "In calls", "Out calls", "In SMS", "Out SMS", "Total"]],
    body: (filteredAnalysis.shortCodesAll || []).slice(0, topN).map((r) => [r.bParty, r.inCall, r.outCall, r.inSms, r.outSms, r.total]),
  }));

  startY = drawPdfSectionTitle(doc, `Other and unresolved B-Parties • Top ${topN}`);
  autoTable(doc, pdfTableOptions({
    startY,
    head: [["B-Party", "In calls", "Out calls", "In SMS", "Out SMS", "Total"]],
    body: (filteredAnalysis.invalidAll || []).slice(0, topN).map((r) => [r.bParty, r.inCall, r.outCall, r.inSms, r.outSms, r.total]),
  }));

  startY = drawPdfSectionTitle(doc, `Most frequent locations • Top ${topN}`);
  autoTable(doc, pdfTableOptions({
    startY,
    head: [["Location", "In calls", "In SMS", "Out calls", "Out SMS", "Total"]],
    body: (filteredAnalysis.mflAll || []).slice(0, topN).map((r) => [r.location, r.inCall, r.inSms, r.outCall, r.outSms, r.total]),
    foot: [["Grand total", mflFooter?.inCall ?? 0, mflFooter?.inSms ?? 0, mflFooter?.outCall ?? 0, mflFooter?.outSms ?? 0, mflFooter?.total ?? 0]],
    showFoot: "lastPage",
    columnStyles: { 0: { cellWidth: 235 } },
  }));

  startY = drawPdfSectionTitle(doc, "IMEI summary");
  autoTable(doc, pdfTableOptions({
    startY,
    head: [["IMEI", "Records", "First seen", "Last seen"]],
    body: (filteredAnalysis.imeiSummary || []).map((r) => [
      r.imei,
      r.count,
      r.from ? r.from.toLocaleString() : "",
      r.to ? r.to.toLocaleString() : "",
    ]),
  }));

  addPdfBranding(doc);
  return doc;
}


export default function CDRStrictExcelAnalyzer_FullMerged() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fileRef = useRef(null);

  const [fileName, setFileName] = useState("");
  const [rawRows, setRawRows] = useState([]);
  const [rawHeaders, setRawHeaders] = useState([]);
  const [parseMeta, setParseMeta] = useState({ headerRowIndex: 0, skippedRows: 0 });

  const [detectedByPrefix, setDetectedByPrefix] = useState("Unknown");
  const [detectedByHeaders, setDetectedByHeaders] = useState("Unknown");

  const [validation, setValidation] = useState(null);
  const [allValidation, setAllValidation] = useState([]);
  const [scientificWarning, setScientificWarning] = useState(null);

  const [processing, setProcessing] = useState(false);
  const [normRows, setNormRows] = useState([]);
  const [analysis, setAnalysis] = useState(null);

  // Filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [imeiFilter, setImeiFilter] = useState("");
  const [bPartySearch, setBPartySearch] = useState("");

  // Top N
  const [topN, setTopN] = useState(20);

  // UI flow
  const [selectedMode, setSelectedMode] = useState("single"); // "single" | "multiple" | "upToFive"
  const [stage, setStage] = useState("initial"); // "initial" | "fileSelected" | "processing" | "done"

  const [tab, setTab] = useState("Upload");

  const rawPreview = useMemo(() => rawRows.slice(0, 500), [rawRows]);
  const rawColumns = useMemo(() => rawHeaders.map((h) => ({ key: h, label: h })), [rawHeaders]);

  const pageStyle = useMemo(
    () => ({ ...styles.page, padding: isMobile ? 12 : 16 }),
    [isMobile]
  );
  const headingMainStyle = useMemo(
    () => ({ ...styles.headingMain, color: theme.palette.text.primary }),
    [theme]
  );
  const headingSectionStyle = useMemo(
    () => ({ ...styles.headingSection, color: theme.palette.text.primary }),
    [theme]
  );
  const mutedStyle = useMemo(
    () => ({ ...styles.muted, color: theme.palette.text.secondary }),
    [theme]
  );
  const okStyle = useMemo(
    () => ({ ...styles.ok, color: theme.palette.success.main }),
    [theme]
  );
  const labelStyle = useMemo(
    () => ({ ...styles.label, color: theme.palette.text.secondary }),
    [theme]
  );
  const listTextColor = useMemo(() => theme.palette.text.primary, [theme]);
  const rowStyle = useMemo(
    () => ({
      ...styles.row,
      flexDirection: isMobile ? "column" : "row",
      alignItems: isMobile ? "stretch" : "center",
    }),
    [isMobile]
  );
  const fieldStyle = useMemo(
    () => ({
      ...styles.field,
      minWidth: isMobile ? "100%" : 200,
      border: `1px solid ${theme.palette.divider}`,
      background: theme.palette.background.paper,
    }),
    [isMobile, theme]
  );
  const inputStyle = useMemo(
    () => ({ ...styles.input, color: theme.palette.text.primary }),
    [theme]
  );
  const headerInfoStyle = useMemo(
    () => ({ flex: 1, minWidth: isMobile ? "100%" : 260 }),
    [isMobile]
  );

  const canAnalyze = validation?.ok && rawRows.length > 0 && !processing && selectedMode === "single";

  const tabs = useMemo(() => {
    const base = ["Upload", "Raw Data"];
    if (stage === "processing") return [...base, "Processing"];
    if (analysis && stage === "done") return [...base, "Insights", "Data Quality", "MFC", "Network Graph", "Timeline", "Short Codes", "Other/Unresolved", "MFL", "Map", "IMEI", "IMEI Scan"];
    return base;
  }, [analysis, stage]);

  const filteredNormRows = useMemo(() => {
    if (!normRows.length) return [];
    const from = fromDate ? new Date(fromDate + "T00:00:00") : null;
    const to = toDate ? new Date(toDate + "T23:59:59") : null;
    const imeiQ = imeiFilter.trim();
    const bQ = bPartySearch.trim();

    return normRows.filter((r) => {
      if (from || to) {
        const dt = r.dateTime instanceof Date ? r.dateTime : null;
        if (!dt) return false;
        if (from && dt < from) return false;
        if (to && dt > to) return false;
      }
      if (imeiQ && !toStr(r.imei).includes(imeiQ)) return false;
      if (bQ && !toStr(r.bParty).includes(bQ)) return false;
      return true;
    });
  }, [normRows, fromDate, toDate, imeiFilter, bPartySearch]);

  const filteredAnalysis = useMemo(() => {
    if (!analysis) return null;
    return analyzeCDR(filteredNormRows);
  }, [analysis, filteredNormRows]);

  const filteredInsights = useMemo(() => (analysis ? analyzeInsights(filteredNormRows) : null), [analysis, filteredNormRows]);

  const mfcRows = useMemo(() => (filteredAnalysis ? filteredAnalysis.mfcAll.slice(0, topN) : []), [filteredAnalysis, topN]);
  const mflRows = useMemo(() => (filteredAnalysis ? filteredAnalysis.mflAll.slice(0, topN) : []), [filteredAnalysis, topN]);
  const scRows = useMemo(() => (filteredAnalysis ? (filteredAnalysis.shortCodesAll || []).slice(0, topN) : []), [filteredAnalysis, topN]);
  const otherRows = useMemo(() => (filteredAnalysis ? (filteredAnalysis.invalidAll || []).slice(0, topN) : []), [filteredAnalysis, topN]);

  const otherTotal = useMemo(
    () => (filteredAnalysis ? (filteredAnalysis.invalidAll || []).reduce((s, r) => s + r.total, 0) : 0),
    [filteredAnalysis]
  );

  const mfcFooter = useMemo(() => {
    if (!filteredAnalysis) return null;
    const sums = filteredAnalysis.mfcAll.reduce(
      (acc, r) => {
        acc.inCall += r.inCall; acc.outCall += r.outCall; acc.inSms += r.inSms; acc.outSms += r.outSms; acc.total += r.total;
        return acc;
      },
      { inCall: 0, outCall: 0, inSms: 0, outSms: 0, total: 0 }
    );
    return { bParty: "Grand Total", ...sums };
  }, [filteredAnalysis]);

  const mflFooter = useMemo(() => {
    if (!filteredAnalysis) return null;
    const sums = filteredAnalysis.mflAll.reduce(
      (acc, r) => {
        acc.inCall += r.inCall; acc.outCall += r.outCall; acc.inSms += r.inSms; acc.outSms += r.outSms; acc.total += r.total;
        return acc;
      },
      { inCall: 0, outCall: 0, inSms: 0, outSms: 0, total: 0 }
    );
    return { location: "Grand Total", ...sums };
  }, [filteredAnalysis]);

  const errorReportText = useMemo(() => {
    if (!validation || validation.ok) return "";
    return buildErrorReportText(validation, fileName, detectedByPrefix, detectedByHeaders);
  }, [validation, fileName, detectedByPrefix, detectedByHeaders]);

  const copyErrorReport = async () => {
    if (!errorReportText) return;
    try {
      await navigator.clipboard.writeText(errorReportText);
      alert("Error report copied.");
    } catch {
      alert("Copy failed (permission). Please download instead.");
    }
  };

  const downloadErrorReport = () => {
    if (!errorReportText) return;
    downloadText(`CDR_Format_Error_Report_${Date.now()}.txt`, errorReportText);
  };

  const clearAnalysisState = () => {
    setFileName("");
    setRawRows([]);
    setRawHeaders([]);
    setParseMeta({ headerRowIndex: 0, skippedRows: 0 });
    setDetectedByPrefix("Unknown");
    setDetectedByHeaders("Unknown");
    setValidation(null);
    setAllValidation([]);
    setScientificWarning(null);
    setParseMeta({ headerRowIndex: 0, skippedRows: 0 });
    setAnalysis(null);
    setNormRows([]);
    setProcessing(false);
    setFromDate("");
    setToDate("");
    setImeiFilter("");
    setBPartySearch("");
    setTopN(20);
    setTab("Upload");
    if (fileRef.current) fileRef.current.value = "";
  };

  const selectMode = (mode) => {
    if (mode === selectedMode) return;
    setSelectedMode(mode);
    clearAnalysisState();
    setStage("initial");
  };

  const processSelectedFile = async (f) => {
    if (!f) return;
    if (!isSupportedDatasetFile(f)) {
      alert("Please select a supported file (.xls, .xlsx, .xlsm, .xlsb, .csv).");
      return;
    }

    setFileName(f.name);
    setProcessing(false);
    setAnalysis(null);
    setNormRows([]);
    setValidation(null);
    setAllValidation([]);
    setDetectedByPrefix("Unknown");
    setDetectedByHeaders("Unknown");
    setScientificWarning(null);
    setTab("Processing");
    setStage("fileSelected");

    setFromDate(""); setToDate(""); setImeiFilter(""); setBPartySearch(""); setTopN(20);

    let dataset;
    try {
      dataset = await loadDatasetFromFile(f);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to read file.");
      return;
    }

    const json = Array.isArray(dataset?.rows) ? dataset.rows : [];
    const headers = Array.isArray(dataset?.headers) ? dataset.headers : [];
    setRawRows(json);
    setRawHeaders(headers);
    setParseMeta({ headerRowIndex: dataset?.headerRowIndex || 0, skippedRows: dataset?.skippedRows || 0 });
    setScientificWarning(getScientificWarningInfo(json, headers));

    const hdrDetect = detectCompanyByHeaders(headers);
    setDetectedByHeaders(hdrDetect.detected);
    setAllValidation(hdrDetect.results);

    // prefix guess
    let prefixGuess = "Unknown";
    const tryCols = ["MSISDN", "Aparty", "A Number", "call_org_num"];
    for (const col of tryCols) {
      if (headers.includes(col)) {
        for (const r of json) {
          const v = normalizeMSISDN(r[col]);
          if (v) { prefixGuess = guessOperatorByPrefix(v); break; }
        }
      }
      if (prefixGuess !== "Unknown") break;
    }
    setDetectedByPrefix(prefixGuess);

    const chosen = hdrDetect.detected === "Unknown" ? "Unknown" : hdrDetect.detected;
    const nextValidation = chosen !== "Unknown"
      ? validateAgainstPattern(headers, chosen)
      : {
        ok: false, company: "Unknown", expectedCount: 0, actualCount: headers.length,
        missing: [], extra: headers, expected: [], actualHeaders: headers,
      };
    setValidation(nextValidation);

    if (nextValidation.ok && selectedMode === "single") {
      await handleAnalyze(json, nextValidation);
    } else {
      setTab("Upload");
    }
  };

  const pickFile = async () => {
    // Use the native file picker with explicit Excel type label when available.
    if (window.showOpenFilePicker) {
      try {
        const [handle] = await window.showOpenFilePicker({
          multiple: false,
          types: [
            {
              description: "CDR Data Files",
              accept: {
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                "application/vnd.ms-excel": [".xls"],
                "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
                "application/vnd.ms-excel.sheet.binary.macroEnabled.12": [".xlsb"],
                "text/csv": [".csv"],
              },
            },
          ],
          excludeAcceptAllOption: true,
        });
        const f = await handle.getFile();
        await processSelectedFile(f);
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }

    if (fileRef.current) {
      fileRef.current.accept = EXCEL_ACCEPT;
      fileRef.current.click();
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    await processSelectedFile(f);
  };

  const handleAnalyze = async (rowsToAnalyze = rawRows, validationToUse = validation) => {
    if (!validationToUse?.ok) {
      setTab("Upload");
      setStage("fileSelected");
      return;
    }
    setProcessing(true);
    setStage("processing");
    setTab("Processing");

    try {
      await new Promise((r) => setTimeout(r, 900));
      const normalized = buildNormalizedRowsByCompany(rowsToAnalyze, validationToUse.company);
      setNormRows(normalized);

      await new Promise((r) => setTimeout(r, 700));
      setAnalysis(analyzeCDR(normalized)); // keeps "analysis" truthy (UI gates)
      setStage("done");
      setTab("Network Graph");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to analyze this file.");
      setStage("fileSelected");
      setTab("Upload");
    } finally {
      setProcessing(false);
    }
  };

  const reset = () => {
    clearAnalysisState();
    setStage("initial");
  };

  const exportToExcel = () => {
    if (!filteredAnalysis) return;
    const wb = XLSX.utils.book_new();

    const insightRows = filteredInsights ? [
      ["Analysis Metadata", "Value"],
      ["Source File", fileName || "-"],
      ["Operator", validation?.company || "Unknown"],
      ["Rows Included", filteredNormRows.length],
      ["Header Row (1-based)", (parseMeta.headerRowIndex || 0) + 1],
      ["Preamble Rows Skipped", parseMeta.skippedRows || 0],
      ["Filter: From", fromDate || "-"],
      ["Filter: To", toDate || "-"],
      ["Filter: IMEI", imeiFilter || "-"],
      ["Filter: B-Party", bPartySearch || "-"],
      [],
      ["Overview", "Value"],
      ["First Seen", formatInsightDate(filteredInsights.overview.firstSeen)],
      ["Last Seen", formatInsightDate(filteredInsights.overview.lastSeen)],
      ["Calls", filteredInsights.overview.calls],
      ["SMS", filteredInsights.overview.sms],
      ["Duration (minutes)", filteredInsights.overview.durationMinutes],
      ["Active Days", filteredInsights.overview.activeDays],
      ["Busiest Hour", filteredInsights.overview.busiestHour],
      ["Busiest Day", filteredInsights.overview.busiestDay],
    ] : [];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(insightRows), "Summary");

    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ["Contact", "Score", "Events", "Active Days", "Directions", "Duration (minutes)"],
      ...filteredInsights.contactNetwork.map((r) => [r.contact, r.score, r.events, r.activeDays, r.directions, r.durationMinutes]),
    ]), "Contact_Network");

    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ["IMEI", "Records", "Unique A-Parties", "Unique Locations", "First Seen", "Last Seen"],
      ...filteredInsights.imeiChanges.map((r) => [r.imei, r.records, r.parties, r.locations, formatInsightDate(r.firstSeen), formatInsightDate(r.lastSeen)]),
    ]), "IMEI_Changes");

    const q = filteredInsights.quality;
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ["Data Quality Check", "Count"],
      ["Rows Included", q.totalRows],
      ["Missing Date/Time", q.missingDateTime],
      ["Missing A-Party", q.missingA],
      ["Missing B-Party", q.missingB],
      ["Missing Location", q.missingLocation],
      ["Missing IMEI", q.missingImei],
      ["Duplicate Rows", q.duplicateRows],
    ]), "Data_Quality");

    const mfcSheetData = [
      ["B Party","Incoming Call","Outgoing Call","Incoming SMS","Outgoing SMS","Grand Total"],
      ...filteredAnalysis.mfcAll.map((r) => [r.bParty, r.inCall, r.outCall, r.inSms, r.outSms, r.total]),
      [],
      ["Grand Total", mfcFooter?.inCall ?? 0, mfcFooter?.outCall ?? 0, mfcFooter?.inSms ?? 0, mfcFooter?.outSms ?? 0, mfcFooter?.total ?? 0],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(mfcSheetData), "MFC");

    // ✅ ShortCodes sheet added (non-breaking)
    const scData = [
      ["Short Code","Incoming Call","Outgoing Call","Incoming SMS","Outgoing SMS","Grand Total"],
      ...(filteredAnalysis.shortCodesAll || []).map((r) => [r.bParty, r.inCall, r.outCall, r.inSms, r.outSms, r.total]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(scData.length ? scData : [["No data"]]), "ShortCodes");

    // ✅ Other/Unresolved sheet — records excluded from MFC/ShortCodes so they're never silently dropped
    const otherData = [
      ["B Party (raw digits)","Incoming Call","Outgoing Call","Incoming SMS","Outgoing SMS","Grand Total"],
      ...(filteredAnalysis.invalidAll || []).map((r) => [r.bParty, r.inCall, r.outCall, r.inSms, r.outSms, r.total]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(otherData.length ? otherData : [["No data"]]), "Other_Unresolved");

    const mflSheetData = [
      ["Location","Incoming Call","Incoming SMS","Outgoing Call","Outgoing SMS","Grand Total"],
      ...filteredAnalysis.mflAll.map((r) => [r.location, r.inCall, r.inSms, r.outCall, r.outSms, r.total]),
      [],
      ["Grand Total", mflFooter?.inCall ?? 0, mflFooter?.inSms ?? 0, mflFooter?.outCall ?? 0, mflFooter?.outSms ?? 0, mflFooter?.total ?? 0],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(mflSheetData), "MFL");

    const imeiSheetData = [
      ["IMEI","Count (Records)","From","To"],
      ...filteredAnalysis.imeiSummary.map((r) => [r.imei, r.count, r.from ? r.from.toLocaleString() : "", r.to ? r.to.toLocaleString() : ""]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(imeiSheetData), "IMEI");

    const scanRows = [];
    filteredAnalysis.imeiScan.forEach((block) => {
      scanRows.push(["IMEI", block.imei, "", ""]);
      scanRows.push(["Mob No","Network (prefix)","Uses",""]);
      block.topNumbers.forEach((n) => scanRows.push([n.msisdn, n.guessedNetwork, n.count, ""]));
      scanRows.push(["", "", "", ""]);
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(scanRows.length ? scanRows : [["No data"]]), "IMEI_Scan");

    XLSX.writeFile(wb, `CDR_Analysis_${Date.now()}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = buildCdrPdfReport({
      filteredAnalysis,
      filteredInsights,
      validation,
      fileName,
      fromDate,
      toDate,
      imeiFilter,
      bPartySearch,
      filteredNormRows,
      parseMeta,
      topN,
      mfcFooter,
      mflFooter,
    });
    if (doc) doc.save(`CDR_Analysis_${Date.now()}.pdf`);
  };

  return (
    <div style={pageStyle}>
      <input ref={fileRef} type="file" accept={EXCEL_ACCEPT} style={{ display: "none" }} onChange={handleFile} />
      <CdrTopAd />

      <SurfaceCard
        sx={{
          p: { xs: 2, sm: 2.5 },
          background: (activeTheme) => `linear-gradient(135deg, ${activeTheme.palette.background.paper} 0%, ${activeTheme.palette.action.hover} 100%)`,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
              CDR Analysis Workspace
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Choose a focused single-file report or compare relationships across several CDR files.
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="outlined"
            color={stage === "done" ? "success" : "default"}
            label={selectedMode === "multiple" ? "Cross-file analysis" : selectedMode === "upToFive" ? "Five-file batch analysis" : stage === "done" ? "Analysis ready" : "Single-file analysis"}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
          <AppButton
            tone={selectedMode === "single" ? "primary" : "ghost"}
            onClick={() => selectMode("single")}
            sx={{ minWidth: { xs: "100%", sm: 180 } }}
          >
            Single CDR Analysis
          </AppButton>
          <AppButton
            tone={selectedMode === "multiple" ? "primary" : "ghost"}
            onClick={() => selectMode("multiple")}
            sx={{ minWidth: { xs: "100%", sm: 180 } }}
          >
            Multiple CDR Analysis
          </AppButton>
          <AppButton
            tone={selectedMode === "upToFive" ? "primary" : "ghost"}
            onClick={() => selectMode("upToFive")}
            sx={{ minWidth: { xs: "100%", sm: 180 } }}
          >
            Up to 5 CDRs Analysis
          </AppButton>
        </Box>
        {selectedMode === "single" && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
            <AppButton tone="ghost" onClick={pickFile}>
              {fileName ? "Replace CDR file" : "Choose CDR file"}
            </AppButton>
          </Box>
        )}
      </SurfaceCard>

      {selectedMode === "multiple" && (
        <SurfaceCard>
          <h2 style={headingSectionStyle}>Multiple CDR Analysis</h2>
          <CDRMultiAnalysis />
        </SurfaceCard>
      )}

      {selectedMode === "upToFive" && (
        <SurfaceCard>
          <h2 style={headingSectionStyle}>Up to 5 CDRs Analysis</h2>
          <CDRUpToFiveAnalysis />
        </SurfaceCard>
      )}

      {selectedMode === "single" && (
        <>
          {stage === "done" && (
            <>
              <h1 style={headingMainStyle}>CDR Analyzer Tool – Call Detail Record Analysis Online</h1>
              <p style={mutedStyle}>
                Upload Excel-based CDRs (Jazz, Zong, Ufone, Telenor) and generate MFC, MFL, IMEI Summary and IMEI Scan reports.
              </p>
            </>
          )}

          {stage === "initial" && (
            <SurfaceCard>
              <div style={styles.centerWrap}>
                <div style={{ width: "100%", maxWidth: 520 }}>
                  <AppButton
                    onClick={pickFile}
                    startIcon={<UploadFileIcon sx={{ fontSize: 28 }} />}
                    sx={{
                      width: "100%",
                      minHeight: 60,
                      px: { xs: 2.5, sm: 5 },
                      py: 1.5,
                      fontSize: { xs: 18, sm: 20 },
                      fontWeight: 900,
                      borderRadius: 3,
                      boxShadow: 4,
                    }}
                  >
                    Upload CDR File
                  </AppButton>
                  <div style={{ marginTop: 10, ...mutedStyle }}>Accepted formats: .xls, .xlsx, .xlsm, .xlsb, .csv</div>
                </div>
              </div>
            </SurfaceCard>
          )}

          {stage !== "initial" && (
            <SurfaceCard>
              <div style={rowStyle}>
                <div style={headerInfoStyle}>
                  <div style={{ fontSize: 18, fontWeight: 900 }}>CDR Strict Analyzer</div>
                  <div style={mutedStyle}>Automatically skips leading customer-summary rows and blank lines, then applies strict header/count checks with diagnostics.</div>
                </div>

                <AppBadge label={`Prefix Guess: ${detectedByPrefix}`} />
                <AppBadge label={`Header Guess: ${detectedByHeaders}`} />

                <AppButton tone="ghost" onClick={pickFile}>{fileName ? "Replace CDR" : "Upload CDR"}</AppButton>

                <AppButton sx={{ opacity: canAnalyze ? 1 : 0.5 }} disabled={!canAnalyze} onClick={() => handleAnalyze()}>
                  {processing ? "Analyzing..." : "Submit & Analyze"}
                </AppButton>

                <AppButton tone="ghost" onClick={reset}>Reset</AppButton>
              </div>

              <div style={{ marginTop: 10 }}>
                {fileName ? <span style={okStyle}>Attached: {fileName}</span> : <span style={mutedStyle}>No file yet.</span>}
              </div>

              {scientificWarning && (
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
                  Scientific ID format detected in <b>{scientificWarning.column}</b> (example: <b>{scientificWarning.sample}</b>).
                  Analyzer will normalize this for calculations. If the source export already rounded values, exact original digits cannot be recovered.
                </Alert>
              )}

              {validation && !validation.ok && (
                <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                  <div style={{ ...styles.danger, marginBottom: 4 }}>❌ Format mismatch for detected company: <b>{validation.company}</b></div>
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    <div><b>Column count mismatch:</b> Expected <b>{validation.expectedCount}</b>, Found <b>{validation.actualCount}</b></div>

                    {validation.missing.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <b>Missing columns (exact):</b>
                        <div style={mutedStyle}>{validation.missing.join(", ")}</div>
                      </div>
                    )}

                    {validation.extra.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <b>Extra columns (not allowed):</b>
                        <div style={mutedStyle}>{validation.extra.join(", ")}</div>
                      </div>
                    )}

                    <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <AppButton tone="danger" onClick={copyErrorReport}>Copy Error Report</AppButton>
                      <AppButton tone="danger" onClick={downloadErrorReport}>Download Error Report (.txt)</AppButton>
                    </div>

                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontWeight: 900, fontSize: 13 }}>Expected columns for {validation.company}:</div>
                      <div style={mutedStyle}>{(PATTERNS[validation.company] || []).join(" | ")}</div>
                    </div>
                  </div>
                </Alert>
              )}

              {allValidation.length > 0 && (!validation || !validation.ok) && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontWeight: 900, marginBottom: 6, fontSize: 13 }}>Pattern match diagnostics</div>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      width: "100%",
                      maxWidth: "100%",
                      overflowX: "auto",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                    }}
                  >
                    <Table size="small" stickyHeader sx={{ minWidth: isMobile ? undefined : 640 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", px: isMobile ? 1 : 2, fontSize: isMobile ? 12 : 14 }}>
                            Company Pattern
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", px: isMobile ? 1 : 2, fontSize: isMobile ? 12 : 14 }}>
                            Expected
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", px: isMobile ? 1 : 2, fontSize: isMobile ? 12 : 14 }}>
                            Found
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", px: isMobile ? 1 : 2, fontSize: isMobile ? 12 : 14 }}>
                            Missing
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", px: isMobile ? 1 : 2, fontSize: isMobile ? 12 : 14 }}>
                            Extra
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allValidation.slice(0, 4).map((r, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ whiteSpace: "nowrap", px: isMobile ? 1 : 2, fontSize: isMobile ? 12 : 14 }}>{r.company}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", px: isMobile ? 1 : 2, fontSize: isMobile ? 12 : 14 }}>{r.expectedCount}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", px: isMobile ? 1 : 2, fontSize: isMobile ? 12 : 14 }}>{r.actualCount}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", px: isMobile ? 1 : 2, fontSize: isMobile ? 12 : 14 }}>{r.missing.length}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", px: isMobile ? 1 : 2, fontSize: isMobile ? 12 : 14 }}>{r.extra.length}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <div style={{ marginTop: 8, ...mutedStyle }}>Shows exactly which operator template is failing and why.</div>
                </div>
              )}

              {validation && validation.ok && (
                <Alert severity="success" sx={{ mt: 1.5, borderRadius: 2 }}>
                  ✅ Format OK for: {validation.company}. You can run analysis.
                </Alert>
              )}
            </SurfaceCard>
          )}

          {stage === "processing" && (
            <SurfaceCard>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <CircularProgress size={18} thickness={5} />
                <div>
                  <div style={{ fontWeight: 900 }}>Analyzing…</div>
                  <div style={mutedStyle}>Strict parsing + grouping contacts/short-codes/locations/IMEI.</div>
                </div>
              </div>
            </SurfaceCard>
          )}

          {(stage === "fileSelected" || stage === "done") && (
            <>
              {stage === "done" && analysis && (
                <SurfaceCard>
                  <div style={{ fontWeight: 900, marginBottom: 10 }}>Filters & Exports</div>
                  <div style={rowStyle}>
                    <div style={fieldStyle}>
                      <div style={labelStyle}>From Date</div>
                      <input style={inputStyle} type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                    </div>
                    <div style={fieldStyle}>
                      <div style={labelStyle}>To Date</div>
                      <input style={inputStyle} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                    </div>
                    <div style={fieldStyle}>
                      <div style={labelStyle}>IMEI Filter (contains)</div>
                      <input style={inputStyle} value={imeiFilter} onChange={(e) => setImeiFilter(e.target.value)} placeholder="e.g., 358..." />
                    </div>
                    <div style={fieldStyle}>
                      <div style={labelStyle}>B-Party Search (contains)</div>
                      <input style={inputStyle} value={bPartySearch} onChange={(e) => setBPartySearch(e.target.value)} placeholder="e.g., 0335..." />
                    </div>

                    <div style={fieldStyle}>
                      <div style={labelStyle}>Top Rows</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <AppButton tone={topN === 10 ? "primary" : "ghost"} onClick={() => setTopN(10)}>Top 10</AppButton>
                        <AppButton tone={topN === 20 ? "primary" : "ghost"} onClick={() => setTopN(20)}>Top 20</AppButton>
                      </div>
                    </div>

                    <AppButton onClick={() => runSponsoredExport(exportToExcel)}>Export Excel</AppButton>
                    <AppButton onClick={() => runSponsoredExport(exportToPDF)}>Export PDF</AppButton>
                    <AppBadge label={`Filtered rows: ${filteredNormRows.length}`} />
                    {otherTotal > 0 && (
                      <AppBadge label={`Other/Unresolved B-Party (see tab): ${otherTotal}`} />
                    )}
                  </div>

                  <div style={{ marginTop: 10, ...mutedStyle }}>
                    Tables reflect filters. Grand Totals are totals of the full filtered dataset.
                  </div>
                </SurfaceCard>
              )}

              <SurfaceCard>
                <div style={styles.tabs}>
                  {tabs.map((t) => (
                    <div key={t} style={styles.tab(tab === t)} onClick={() => setTab(t)}>{t}</div>
                  ))}
                </div>

                <div style={{ marginTop: 12 }}>
                  {tab === "Upload" && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8, color: listTextColor }}>Upload</div>
                      <div style={{ ...mutedStyle, marginBottom: 10 }}>
                        {fileName ? `Attached: ${fileName}` : "No file attached."}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                        <AppButton tone="ghost" onClick={pickFile}>{fileName ? "Replace CDR" : "Upload CDR"}</AppButton>
                        <AppButton sx={{ opacity: canAnalyze ? 1 : 0.5 }} disabled={!canAnalyze} onClick={() => handleAnalyze()}>
                          {processing ? "Analyzing..." : "Submit & Analyze"}
                        </AppButton>
                        <AppButton tone="ghost" onClick={reset}>Reset</AppButton>
                      </div>
                      <div style={{ fontWeight: 900, color: listTextColor }}>Rules</div>
                      <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7, fontSize: 14, color: listTextColor }}>
                        <li>Strict templates: exact column names + exact column count.</li>
                        <li>If mismatch: exact error + missing/extra + downloadable error report.</li>
                        <li>Raw Excel preview always visible.</li>
                        <li>MFC dedupe: 0092/92/03 variants merged. Short codes separated.</li>
                      </ul>
                    </div>
                  )}

                  {tab === "Processing" && (
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <CircularProgress size={18} thickness={5} />
                      <div>
                        <div style={{ fontWeight: 900 }}>Analyzing…</div>
                        <div style={mutedStyle}>Strict parsing + grouping contacts/short-codes/locations/IMEI.</div>
                      </div>
                    </div>
                  )}

                  {tab === "Insights" && filteredInsights && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 10 }}>Analysis Insights</div>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 10 }}>
                        {[
                          ["First Seen", formatInsightDate(filteredInsights.overview.firstSeen)],
                          ["Last Seen", formatInsightDate(filteredInsights.overview.lastSeen)],
                          ["Active Days", filteredInsights.overview.activeDays],
                          ["Calls", filteredInsights.overview.calls],
                          ["SMS", filteredInsights.overview.sms],
                          ["Duration", `${filteredInsights.overview.durationMinutes} min`],
                          ["Busiest Hour", filteredInsights.overview.busiestHour],
                          ["Busiest Day", filteredInsights.overview.busiestDay],
                          ["Burst Windows", filteredInsights.burstWindows.length],
                        ].map(([label, value]) => (
                          <div key={label} style={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 8, padding: 10 }}>
                            <div style={labelStyle}>{label}</div>
                            <div style={{ fontWeight: 900, color: listTextColor }}>{value}</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ fontWeight: 900, margin: "16px 0 8px" }}>Contact Network Scores</div>
                      <SimpleTableWithFooter
                        columns={[
                          { key: "contact", label: "Contact" },
                          { key: "score", label: "Relationship Score" },
                          { key: "events", label: "Events" },
                          { key: "activeDays", label: "Active Days" },
                          { key: "directions", label: "Directions" },
                          { key: "durationMinutes", label: "Minutes" },
                        ]}
                        rows={filteredInsights.contactNetwork.slice(0, topN).map((r, i) => ({ ...r, __id: i }))}
                        emptyText="No contact network data (check filters)."
                      />

                      <div style={{ fontWeight: 900, margin: "16px 0 8px" }}>Burst Activity Windows</div>
                      <SimpleTableWithFooter
                        columns={[
                          { key: "start", label: "Start", render: (r) => formatInsightDate(r.start) },
                          { key: "end", label: "End", render: (r) => formatInsightDate(r.end) },
                          { key: "events", label: "Events in 60 Minutes" },
                        ]}
                        rows={filteredInsights.burstWindows.map((r, i) => ({ ...r, __id: i }))}
                        emptyText="No burst windows detected."
                      />
                    </div>
                  )}

                  {tab === "Data Quality" && filteredInsights && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>Data Quality & Provenance</div>
                      <div style={{ ...mutedStyle, marginBottom: 12 }}>
                        Header row: {(parseMeta.headerRowIndex || 0) + 1}. Preamble/summary rows skipped: {parseMeta.skippedRows || 0}. These values are included in exports for reproducibility.
                      </div>
                      <SimpleTableWithFooter
                        columns={[{ key: "check", label: "Check" }, { key: "count", label: "Rows" }, { key: "status", label: "Status" }]}
                        rows={[
                          ["Rows included", filteredInsights.quality.totalRows],
                          ["Missing date/time", filteredInsights.quality.missingDateTime],
                          ["Missing A-party", filteredInsights.quality.missingA],
                          ["Missing B-party", filteredInsights.quality.missingB],
                          ["Missing location", filteredInsights.quality.missingLocation],
                          ["Missing IMEI", filteredInsights.quality.missingImei],
                          ["Duplicate rows", filteredInsights.quality.duplicateRows],
                        ].map(([check, count], i) => ({ __id: i, check, count, status: count === 0 || check === "Rows included" ? "OK" : "Review" }))}
                        emptyText="No quality data."
                      />
                      <div style={{ fontWeight: 900, margin: "16px 0 8px" }}>IMEI / Identity Changes</div>
                      <SimpleTableWithFooter
                        columns={[
                          { key: "imei", label: "IMEI" },
                          { key: "records", label: "Records" },
                          { key: "parties", label: "A-Parties" },
                          { key: "locations", label: "Locations" },
                          { key: "firstSeen", label: "First Seen", render: (r) => formatInsightDate(r.firstSeen) },
                          { key: "lastSeen", label: "Last Seen", render: (r) => formatInsightDate(r.lastSeen) },
                        ]}
                        rows={filteredInsights.imeiChanges.map((r, i) => ({ ...r, __id: i }))}
                        emptyText="No IMEI or identity changes detected."
                      />
                    </div>
                  )}

                  {tab === "Raw Data" && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>Raw Excel View (first {rawPreview.length} rows)</div>
                      <SimpleTableWithFooter columns={rawColumns} rows={rawPreview} emptyText="Upload an Excel to preview raw data." />
                      {rawRows.length > 500 && <div style={{ marginTop: 8, ...mutedStyle }}>Showing first 500 rows for UI performance.</div>}
                    </div>
                  )}

                  {tab === "MFC" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>MFC (Most Frequent Contacts) — Showing {topN}</div>
                      <SimpleTableWithFooter
                        columns={[
                          { key: "bParty", label: "B Party (Canonical)" },
                          { key: "inCall", label: "Incoming Call" },
                          { key: "outCall", label: "Outgoing Call" },
                          { key: "inSms", label: "Incoming SMS" },
                          { key: "outSms", label: "Outgoing SMS" },
                          { key: "total", label: "Grand Total" },
                        ]}
                        rows={mfcRows.map((r, i) => ({ ...r, __id: i }))}
                        footer={mfcFooter}
                        emptyText="No data (check filters)."
                      />
                    </div>
                  )}

                  {tab === "Network Graph" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>Network Graph — Contacts &amp; Devices (Showing {topN})</div>
                      <CDRNetworkGraph
                        filteredAnalysis={filteredAnalysis}
                        filteredNormRows={filteredNormRows}
                        topN={topN}
                        onSelectContact={(num) => setBPartySearch(num)}
                        onSelectDevice={(imei) => setImeiFilter(imei)}
                      />
                      <div style={{ marginTop: 8, ...mutedStyle }}>
                        Click a contact to fill B-Party Search, or a device to fill the IMEI filter, then check Raw Data.
                      </div>
                    </div>
                  )}

                  {tab === "Timeline" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>Timeline — Contact Activity (Showing {topN})</div>
                      <CDRTimeline
                        filteredAnalysis={filteredAnalysis}
                        filteredNormRows={filteredNormRows}
                        filteredInsights={filteredInsights}
                        topN={topN}
                        onSelectContact={(num) => setBPartySearch(num)}
                      />
                      <div style={{ marginTop: 8, ...mutedStyle }}>
                        Click a mark to fill B-Party Search, then check Raw Data. Shaded bands mark burst windows (8+ events within 60 minutes).
                      </div>
                    </div>
                  )}

                  {tab === "Short Codes" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>Short Codes / Service IDs — Showing {topN}</div>
                      <SimpleTableWithFooter
                        columns={[
                          { key: "bParty", label: "Short Code" },
                          { key: "inCall", label: "Incoming Call" },
                          { key: "outCall", label: "Outgoing Call" },
                          { key: "inSms", label: "Incoming SMS" },
                          { key: "outSms", label: "Outgoing SMS" },
                          { key: "total", label: "Grand Total" },
                        ]}
                        rows={scRows.map((r, i) => ({ ...r, __id: i }))}
                        emptyText="No short codes (check filters)."
                      />
                      <div style={{ marginTop: 8, ...mutedStyle }}>
                        Note: short codes are separated to avoid inflating MFC contacts.
                      </div>
                    </div>
                  )}

                  {tab === "Other/Unresolved" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>Other / Unresolved B-Party — Showing {topN}</div>
                      <SimpleTableWithFooter
                        columns={[
                          { key: "bParty", label: "B Party (raw digits)" },
                          { key: "inCall", label: "Incoming Call" },
                          { key: "outCall", label: "Outgoing Call" },
                          { key: "inSms", label: "Incoming SMS" },
                          { key: "outSms", label: "Outgoing SMS" },
                          { key: "total", label: "Grand Total" },
                        ]}
                        rows={otherRows.map((r, i) => ({ ...r, __id: i }))}
                        emptyText="No unresolved records (check filters)."
                      />
                      <div style={{ marginTop: 8, ...mutedStyle }}>
                        These B-Party values didn't match a valid local MSISDN (11 digits) or short code (≤8 digits) —
                        e.g. blank fields, data/GPRS sessions, malformed exports, or landline/international numbers.
                        They are excluded from MFC and Short Codes so those reports aren't polluted, but are listed
                        here so no record is silently dropped from the analysis. Total unresolved: {otherTotal}.
                      </div>
                    </div>
                  )}

                  {tab === "MFL" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>MFL (Most Frequent Locations) — Showing {topN}</div>
                      <SimpleTableWithFooter
                        columns={[
                          { key: "location", label: "Location" },
                          { key: "inCall", label: "Incoming Call" },
                          { key: "inSms", label: "Incoming SMS" },
                          { key: "outCall", label: "Outgoing Call" },
                          { key: "outSms", label: "Outgoing SMS" },
                          { key: "total", label: "Grand Total" },
                        ]}
                        rows={mflRows.map((r, i) => ({ ...r, __id: i }))}
                        footer={mflFooter}
                        emptyText="No data (check filters)."
                      />
                    </div>
                  )}

                  {tab === "Map" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>Map — Locations by Activity (Showing {topN})</div>
                      <CDRMap
                        filteredAnalysis={filteredAnalysis}
                        filteredNormRows={filteredNormRows}
                        topN={topN}
                      />
                    </div>
                  )}

                  {tab === "IMEI" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>IMEI Summary</div>
                      <SimpleTableWithFooter
                        columns={[
                          { key: "imei", label: "IMEI" },
                          { key: "count", label: "Count (Records)" },
                          { key: "from", label: "From", render: (r) => (r.from ? r.from.toLocaleString() : "") },
                          { key: "to", label: "To", render: (r) => (r.to ? r.to.toLocaleString() : "") },
                        ]}
                        rows={filteredAnalysis.imeiSummary.map((r, i) => ({ ...r, __id: i }))}
                        emptyText="No IMEI values (check filters)."
                      />
                    </div>
                  )}

                  {tab === "IMEI Scan" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>IMEI Scan</div>
                      <div style={{ display: "grid", gap: 12 }}>
                        {filteredAnalysis.imeiScan.map((block, idx) => (
                          <div key={block.imei + idx} style={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 12, padding: 12 }}>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                              <div style={{ fontWeight: 900 }}>IMEI: {block.imei}</div>
                              <AppBadge label={`Top Numbers shown: ${Math.min(15, block.topNumbers.length)}`} />
                            </div>
                            <div style={{ marginTop: 10 }}>
                              <SimpleTableWithFooter
                                columns={[
                                  { key: "msisdn", label: "Mob No" },
                                  { key: "guessedNetwork", label: "Network (by prefix)" },
                                  { key: "count", label: "Uses" },
                                ]}
                                rows={block.topNumbers.slice(0, 15).map((r, i) => ({ ...r, __id: `${idx}-${i}` }))}
                                emptyText="No numbers for this IMEI."
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!filteredAnalysis && (tab === "Insights" || tab === "Data Quality" || tab === "MFC" || tab === "Network Graph" || tab === "Timeline" || tab === "Short Codes" || tab === "Other/Unresolved" || tab === "MFL" || tab === "Map" || tab === "IMEI" || tab === "IMEI Scan") && (
                    <div style={mutedStyle}>Analysis not generated yet (or disabled due to format mismatch).</div>
                  )}
                </div>
              </SurfaceCard>
            </>
          )}

          {stage === "done" && (
            <SurfaceCard>
              <h2 style={headingSectionStyle}>What is a CDR Analyzer?</h2>
              <p style={mutedStyle}>
                A CDR Analyzer tool helps investigators and analysts examine Call Detail Records (CDR) to identify frequent contacts,
                call patterns, IMEI usage, and location history. This tool validates operator-specific Excel formats and generates
                investigation-ready summaries.
              </p>

              <h2 style={headingSectionStyle}>Supported Telecom Operators</h2>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7, color: listTextColor }}>
                <li>Jazz CDR Excel</li>
                <li>Zong CDR Excel</li>
                <li>Ufone CDR Excel</li>
                <li>Telenor CDR Excel</li>
              </ul>

              <h2 style={headingSectionStyle}>Key Features</h2>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7, color: listTextColor }}>
                <li>Strict column validation with downloadable error report</li>
                <li>Filters: date range, IMEI filter, B-party search</li>
                <li>Top 10 / Top 20 toggle and Grand Totals</li>
                <li>Export results to Excel and PDF</li>
              </ul>
            </SurfaceCard>
          )}
        </>
      )}

      <CdrBannerAd placement="mid" />
      <CdrFeaturesGuide />
      <CdrBannerAd placement="end" />
    </div>
  );
}
